/**
 * Template Questions Configuration for PE Diligence
 *
 * Structured questions by company type with:
 * - Priority levels for importance ranking
 * - Answer formats for consistent presentation
 * - Follow-up topics for deeper research
 * - Web research requirement flags
 */

export type Priority = 'critical' | 'high' | 'medium' | 'low'
export type AnswerFormat = 'narrative' | 'bullets' | 'table' | 'metrics'
export type CompanyTypeKey = 'EQUITY' | 'ETF' | 'MUTUALFUND' | 'PRIVATE'

export interface TemplateQuestion {
  id: string
  question: string
  priority: Priority
  format: AnswerFormat
  followUpTopics?: string[]
  requiresWebResearch?: boolean
  category: string
}

// ═══════════════════════════════════════════════════════════════════════════════
// EQUITY (Public Company) Questions
// ═══════════════════════════════════════════════════════════════════════════════

export const EQUITY_QUESTIONS: TemplateQuestion[] = [
  // Financial Performance - Critical
  {
    id: 'eq-financial-trends',
    question: 'What are the key financial trends over the past 5 years?',
    priority: 'critical',
    format: 'table',
    category: 'Financial',
    followUpTopics: ['Revenue drivers', 'Margin trends', 'Seasonal patterns'],
  },
  {
    id: 'eq-fcf-sustainability',
    question: 'How sustainable is the current FCF generation?',
    priority: 'critical',
    format: 'metrics',
    category: 'Financial',
    followUpTopics: ['Working capital trends', 'CapEx requirements', 'Cash conversion'],
  },
  {
    id: 'eq-debt-analysis',
    question: 'What is the debt structure and covenant situation?',
    priority: 'critical',
    format: 'table',
    category: 'Financial',
    followUpTopics: ['Maturity schedule', 'Interest coverage', 'Refinancing risk'],
  },

  // Competitive Position - Critical
  {
    id: 'eq-competitive-landscape',
    question: 'What is the competitive landscape and market share dynamics?',
    priority: 'critical',
    format: 'bullets',
    category: 'Competition',
    requiresWebResearch: true,
    followUpTopics: ['Key competitors', 'Market positioning', 'Competitive moats'],
  },
  {
    id: 'eq-customer-concentration',
    question: 'What is the customer concentration risk?',
    priority: 'critical',
    format: 'table',
    category: 'Risk',
    followUpTopics: ['Top 10 customers', 'Contract terms', 'Churn rates'],
  },

  // Risk Assessment - High
  {
    id: 'eq-key-risks',
    question: 'What are the key risks to the business model?',
    priority: 'high',
    format: 'bullets',
    category: 'Risk',
    requiresWebResearch: true,
    followUpTopics: ['Regulatory risks', 'Technology disruption', 'Supply chain'],
  },
  {
    id: 'eq-management-quality',
    question: 'What is the track record of the management team?',
    priority: 'high',
    format: 'narrative',
    category: 'Management',
    requiresWebResearch: true,
    followUpTopics: ['Tenure', 'Previous roles', 'Capital allocation history'],
  },
  {
    id: 'eq-litigation',
    question: 'Are there any material litigations or regulatory issues?',
    priority: 'high',
    format: 'bullets',
    category: 'Risk',
    requiresWebResearch: true,
    followUpTopics: ['Pending cases', 'Settlement history', 'Compliance issues'],
  },

  // Growth & Strategy - High
  {
    id: 'eq-growth-strategy',
    question: 'What is the organic growth strategy and runway?',
    priority: 'high',
    format: 'narrative',
    category: 'Strategy',
    followUpTopics: ['Market expansion', 'New products', 'Pricing power'],
  },
  {
    id: 'eq-ma-history',
    question: 'What is the M&A track record and integration capability?',
    priority: 'high',
    format: 'table',
    category: 'Strategy',
    requiresWebResearch: true,
    followUpTopics: ['Recent acquisitions', 'Integration success', 'Deal multiples'],
  },

  // Operations - Medium
  {
    id: 'eq-supply-chain',
    question: 'How resilient is the supply chain?',
    priority: 'medium',
    format: 'bullets',
    category: 'Operations',
    requiresWebResearch: true,
    followUpTopics: ['Key suppliers', 'Geographic concentration', 'Alternatives'],
  },
  {
    id: 'eq-technology',
    question: 'What is the technology stack and modernization status?',
    priority: 'medium',
    format: 'narrative',
    category: 'Operations',
    followUpTopics: ['IT investments', 'Legacy systems', 'Cybersecurity'],
  },

  // ESG - Medium
  {
    id: 'eq-esg',
    question: 'What are the key ESG considerations and initiatives?',
    priority: 'medium',
    format: 'bullets',
    category: 'ESG',
    requiresWebResearch: true,
    followUpTopics: ['Environmental footprint', 'Social initiatives', 'Governance'],
  },
]

// ═══════════════════════════════════════════════════════════════════════════════
// ETF Questions
// ═══════════════════════════════════════════════════════════════════════════════

export const ETF_QUESTIONS: TemplateQuestion[] = [
  // Fund Basics - Critical
  {
    id: 'etf-strategy',
    question: 'What is the ETF\'s investment strategy and benchmark?',
    priority: 'critical',
    format: 'narrative',
    category: 'Overview',
    followUpTopics: ['Index methodology', 'Replication method', 'Investment process'],
  },
  {
    id: 'etf-holdings',
    question: 'What are the top 10 holdings and sector allocations?',
    priority: 'critical',
    format: 'table',
    category: 'Holdings',
    followUpTopics: ['Concentration risk', 'Turnover', 'Rebalancing frequency'],
  },
  {
    id: 'etf-expense',
    question: 'What is the expense ratio compared to similar ETFs?',
    priority: 'critical',
    format: 'metrics',
    category: 'Costs',
    requiresWebResearch: true,
    followUpTopics: ['Total cost of ownership', 'Securities lending income', 'Fee waivers'],
  },

  // Performance - High
  {
    id: 'etf-tracking',
    question: 'What is the tracking error vs the benchmark?',
    priority: 'high',
    format: 'metrics',
    category: 'Performance',
    followUpTopics: ['Sources of tracking difference', 'Historical tracking', 'Premium/discount'],
  },
  {
    id: 'etf-performance',
    question: 'How has the ETF performed over various time periods?',
    priority: 'high',
    format: 'table',
    category: 'Performance',
    followUpTopics: ['1Y, 3Y, 5Y returns', 'Max drawdown', 'Risk-adjusted returns'],
  },

  // Liquidity & Trading - High
  {
    id: 'etf-liquidity',
    question: 'What is the trading volume and liquidity profile?',
    priority: 'high',
    format: 'metrics',
    category: 'Trading',
    followUpTopics: ['Average daily volume', 'Bid-ask spread', 'Market maker quality'],
  },
  {
    id: 'etf-aum',
    question: 'What is the AUM and fund flow trend?',
    priority: 'high',
    format: 'metrics',
    category: 'Overview',
    followUpTopics: ['AUM growth', 'Recent flows', 'Closure risk'],
  },

  // Risk - Medium
  {
    id: 'etf-risk-metrics',
    question: 'What are the key risk metrics (beta, volatility, Sharpe)?',
    priority: 'medium',
    format: 'table',
    category: 'Risk',
    followUpTopics: ['Standard deviation', 'Downside capture', 'Correlation'],
  },
  {
    id: 'etf-alternatives',
    question: 'What are the comparable ETFs and how does this compare?',
    priority: 'medium',
    format: 'table',
    category: 'Alternatives',
    requiresWebResearch: true,
    followUpTopics: ['Direct competitors', 'Fee comparison', 'Strategy differences'],
  },
]

// ═══════════════════════════════════════════════════════════════════════════════
// Mutual Fund Questions
// ═══════════════════════════════════════════════════════════════════════════════

export const MUTUALFUND_QUESTIONS: TemplateQuestion[] = [
  // Fund & Manager - Critical
  {
    id: 'mf-objective',
    question: 'What is the fund\'s investment objective and strategy?',
    priority: 'critical',
    format: 'narrative',
    category: 'Overview',
    followUpTopics: ['Prospectus language', 'Investment process', 'Style consistency'],
  },
  {
    id: 'mf-manager',
    question: 'What is the portfolio manager\'s track record?',
    priority: 'critical',
    format: 'narrative',
    category: 'Manager',
    requiresWebResearch: true,
    followUpTopics: ['Manager tenure', 'Previous funds', 'Team stability'],
  },
  {
    id: 'mf-fees',
    question: 'What is the total cost structure (expense ratio, loads, 12b-1)?',
    priority: 'critical',
    format: 'table',
    category: 'Costs',
    followUpTopics: ['Share class comparison', 'Fee waivers', 'Breakpoints'],
  },

  // Holdings & Style - High
  {
    id: 'mf-holdings',
    question: 'What is the current portfolio composition?',
    priority: 'high',
    format: 'table',
    category: 'Holdings',
    followUpTopics: ['Top 10 holdings', 'Sector weights', 'Geographic allocation'],
  },
  {
    id: 'mf-style',
    question: 'Has there been any style drift over time?',
    priority: 'high',
    format: 'narrative',
    category: 'Style',
    followUpTopics: ['Style box history', 'Benchmark changes', 'Mandate adherence'],
  },

  // Performance - High
  {
    id: 'mf-performance',
    question: 'How has the fund performed vs benchmark and peers?',
    priority: 'high',
    format: 'table',
    category: 'Performance',
    followUpTopics: ['Category ranking', 'Consistency', 'Up/down capture'],
  },
  {
    id: 'mf-turnover',
    question: 'What is the portfolio turnover and tax efficiency?',
    priority: 'high',
    format: 'metrics',
    category: 'Tax',
    followUpTopics: ['Turnover ratio', 'Capital gains distributions', 'Tax cost ratio'],
  },

  // Risk & Alternatives - Medium
  {
    id: 'mf-risk',
    question: 'What are the key risk metrics?',
    priority: 'medium',
    format: 'table',
    category: 'Risk',
    followUpTopics: ['Standard deviation', 'Sharpe ratio', 'Max drawdown'],
  },
  {
    id: 'mf-alternatives',
    question: 'What are comparable funds with better cost/performance?',
    priority: 'medium',
    format: 'table',
    category: 'Alternatives',
    requiresWebResearch: true,
    followUpTopics: ['Lower cost options', 'ETF alternatives', 'Peer comparison'],
  },
]

// ═══════════════════════════════════════════════════════════════════════════════
// Private Company Questions
// ═══════════════════════════════════════════════════════════════════════════════

export const PRIVATE_QUESTIONS: TemplateQuestion[] = [
  // Ownership & Control - Critical
  {
    id: 'priv-ownership',
    question: 'What is the ownership structure and cap table?',
    priority: 'critical',
    format: 'table',
    category: 'Ownership',
    requiresWebResearch: true,
    followUpTopics: ['Shareholders', 'Voting rights', 'Preferred terms'],
  },
  {
    id: 'priv-management',
    question: 'Who are the key executives and their backgrounds?',
    priority: 'critical',
    format: 'bullets',
    category: 'Management',
    requiresWebResearch: true,
    followUpTopics: ['LinkedIn profiles', 'Prior companies', 'Industry experience'],
  },
  {
    id: 'priv-financials',
    question: 'What are the estimated financials (revenue, EBITDA)?',
    priority: 'critical',
    format: 'metrics',
    category: 'Financial',
    requiresWebResearch: true,
    followUpTopics: ['Revenue range', 'Profitability', 'Growth rate'],
  },

  // Market Position - Critical
  {
    id: 'priv-market',
    question: 'What is the company\'s market position and share?',
    priority: 'critical',
    format: 'narrative',
    category: 'Market',
    requiresWebResearch: true,
    followUpTopics: ['Competitors', 'Market size', 'Growth trends'],
  },
  {
    id: 'priv-products',
    question: 'What are the main products/services and their differentiation?',
    priority: 'critical',
    format: 'bullets',
    category: 'Products',
    requiresWebResearch: true,
    followUpTopics: ['Product lines', 'Pricing', 'Unique value proposition'],
  },

  // Operations - High
  {
    id: 'priv-customers',
    question: 'Who are the key customers and what is the concentration?',
    priority: 'high',
    format: 'table',
    category: 'Customers',
    requiresWebResearch: true,
    followUpTopics: ['Customer list', 'Contract terms', 'Revenue breakdown'],
  },
  {
    id: 'priv-employees',
    question: 'What is the employee count and key talent profile?',
    priority: 'high',
    format: 'metrics',
    category: 'Operations',
    requiresWebResearch: true,
    followUpTopics: ['Headcount', 'Key hires', 'Retention'],
  },

  // Funding & History - High
  {
    id: 'priv-funding',
    question: 'What is the funding history and investor base?',
    priority: 'high',
    format: 'table',
    category: 'Funding',
    requiresWebResearch: true,
    followUpTopics: ['Funding rounds', 'Valuation', 'Notable investors'],
  },
  {
    id: 'priv-news',
    question: 'What are recent news and press releases?',
    priority: 'high',
    format: 'bullets',
    category: 'News',
    requiresWebResearch: true,
    followUpTopics: ['Recent articles', 'Awards', 'Partnerships'],
  },

  // Risk & Legal - Medium
  {
    id: 'priv-litigation',
    question: 'Are there any known legal issues or liabilities?',
    priority: 'medium',
    format: 'bullets',
    category: 'Risk',
    requiresWebResearch: true,
    followUpTopics: ['Court records', 'Regulatory issues', 'Liens'],
  },
  {
    id: 'priv-ip',
    question: 'What intellectual property does the company own?',
    priority: 'medium',
    format: 'bullets',
    category: 'IP',
    requiresWebResearch: true,
    followUpTopics: ['Patents', 'Trademarks', 'Trade secrets'],
  },
]

// ═══════════════════════════════════════════════════════════════════════════════
// Helper Functions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get questions for a company type
 */
export function getQuestionsForType(companyType: CompanyTypeKey): TemplateQuestion[] {
  switch (companyType) {
    case 'ETF':
      return ETF_QUESTIONS
    case 'MUTUALFUND':
      return MUTUALFUND_QUESTIONS
    case 'PRIVATE':
      return PRIVATE_QUESTIONS
    case 'EQUITY':
    default:
      return EQUITY_QUESTIONS
  }
}

/**
 * Get questions by priority
 */
export function getQuestionsByPriority(
  questions: TemplateQuestion[],
  priority: Priority
): TemplateQuestion[] {
  return questions.filter(q => q.priority === priority)
}

/**
 * Get questions requiring web research
 */
export function getWebResearchQuestions(questions: TemplateQuestion[]): TemplateQuestion[] {
  return questions.filter(q => q.requiresWebResearch)
}

/**
 * Get questions by category
 */
export function getQuestionsByCategory(
  questions: TemplateQuestion[],
  category: string
): TemplateQuestion[] {
  return questions.filter(q => q.category === category)
}

/**
 * Get suggested chat questions for a company type (top 5 most critical)
 */
export function getSuggestedChatQuestions(companyType: CompanyTypeKey): string[] {
  const questions = getQuestionsForType(companyType)
  const critical = questions.filter(q => q.priority === 'critical')
  const high = questions.filter(q => q.priority === 'high')

  const suggested = [...critical, ...high].slice(0, 5)
  return suggested.map(q => q.question)
}
